import { CountBadge } from '@vrplatform/components';
import {
  useBilling,
  useDashboard,
  useEnabledFeatures,
  useIsPlaywright,
  useMe,
  useQuery,
  useSubscription,
  useTeam,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { IconButton } from '@vrplatform/components-legacy';
import {
  Box,
  LoadingOverlay,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import {
  type TenantSetupGuide,
  XIMPLIFI_TENANT_ID,
} from '@vrplatform/ui-common';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { HiddenFeatureIndicator } from '../components';
import { useNavbarExpanded, useTeamIssues } from '../hooks';
import { NavbarRoute } from '../layout/navbar';
import {
  getPropertyManagementConnections,
  useLegacyIssuesQuery,
} from '@vrplatform/data';
import { useSetupGuideStore } from '../stores/useSetupGuideStore';
import { useStatementAutomation } from '../views/statements/legacy/_hooks';
import { NavigationSection as Section } from './NavigationSection';
import { NavbarSyncsIconButton } from './_components';
import { useMemo } from 'react';

const ICON_SIZE = 18;

export const PmNavigation = () => {
  const [{ isGL: GL }] = useTeam();

  if (GL) {
    return <Ledger />;
  }

  return <Legacy />;
};

const Legacy = () => {
  const { isReadOnly } = useBilling();
  const [searchParams] = useSearchParams();

  const { email: userEmail } = useMe();

  const theme = useMantineTheme();

  const { hasStatementAutomations, isInitialLoading } =
    useStatementAutomation();

  const isPlaywright = useIsPlaywright(userEmail);

  const getCurrentStatementsLink = () => {
    const base = '/statements/owner-statements';

    if (location.pathname.startsWith('/statement')) {
      const date = searchParams.get('date');

      return `${base}${date ? `?date=${date}` : ''}`;
    }

    return base;
  };

  const hasOwnerStatementAutomation = isPlaywright || hasStatementAutomations;

  return (
    <>
      <LoadingOverlay
        visible={isInitialLoading}
        overlayProps={{
          color: theme.colors[theme.primaryColor][8],
          opacity: 0.8,
          blur: 50,
        }}
        loaderProps={{
          color: '#fff',
          size: 'sm',
        }}
      />

      <HiddenFeatureIndicator permission="SETUP_GUIDE_UI">
        <UserGuide />
      </HiddenFeatureIndicator>

      <Section>
        <NavbarRoute
          title="Dashboard"
          link="/"
          icon={<Icon icon="DashboardIcon" size={ICON_SIZE} />}
          notification={isReadOnly ? null : <LegacyIssueCountBadge />}
        />
      </Section>

      <Section>
        <NavbarRoute
          title={'Payments'}
          link={'/payments'}
          icon={<Icon icon={'CircleDollarIcon'} size={ICON_SIZE} />}
        />

        <NavbarRoute
          title="Reservations"
          link="/reservations"
          icon={<Icon icon="CalendarEventIcon" size={ICON_SIZE} />}
        />
        <NavbarRoute
          title="Listings"
          link="/listings"
          icon={<Icon icon="HomeIcon" size={ICON_SIZE} />}
        />

        {hasOwnerStatementAutomation && (
          <NavbarRoute
            title={'Owners'}
            link={'/owners'}
            icon={<Icon icon="UserIcon" size={ICON_SIZE} />}
          />
        )}

        <NavbarRoute
          title="Mappings"
          link="/mappings"
          icon={<Icon icon="RouteIcon" size={ICON_SIZE} />}
        />

        {hasOwnerStatementAutomation && (
          <NavbarRoute
            title="Custom Fees"
            link="/custom-fees"
            icon={<Icon icon="PercentageIcon" size={ICON_SIZE} />}
          />
        )}

        <NavbarRoute
          title="Automations"
          link="/automations"
          icon={<Icon icon="AutomationsIcon" size={ICON_SIZE} />}
        />

        {hasOwnerStatementAutomation && (
          <NavbarRoute
            title="Statements"
            link={getCurrentStatementsLink()}
            icon={<Icon icon="ClipboardTextIcon" size={ICON_SIZE} />}
          />
        )}
      </Section>

      <Section>
        <NavbarRoute
          title="Connections"
          link="/connections"
          icon={<Icon icon="ShareIcon" size={ICON_SIZE} />}
        />

        {hasOwnerStatementAutomation && (
          <NavbarRoute
            title="Files"
            link="/files"
            icon={<Icon icon={'FolderOpenIcon'} size={ICON_SIZE} />}
          />
        )}
        <NavbarRoute
          title="Settings"
          link="/settings"
          icon={<Icon icon="GearIcon" size={ICON_SIZE} />}
        />
      </Section>

      <LegacyMappings />
    </>
  );
};

function usePageType() {
  const location = useLocation();

  return useMemo(() => {
    if (
      location.pathname.startsWith('/statements') ||
      location.pathname.startsWith('/statement/') ||
      location.pathname.startsWith('/pm-statement/')
    ) {
      return 'statements';
    }

    if (
      location.pathname.startsWith('/deposits') ||
      location.pathname.startsWith('/deposit/')
    ) {
      return 'deposits';
    }

    if (
      location.pathname.startsWith('/expenses') ||
      location.pathname.startsWith('/expense/') ||
      location.pathname.startsWith('/recurring/')
    ) {
      return 'expenses';
    }

    if (
      location.pathname.startsWith('/owners') ||
      location.pathname.startsWith('/owner/')
    ) {
      return 'owners';
    }

    if (
      location.pathname.startsWith('/listings') ||
      location.pathname.startsWith('/listing/')
    ) {
      return 'listings';
    }

    if (
      location.pathname.startsWith('/fees-commissions') ||
      location.pathname.startsWith('/fees-commission/')
    ) {
      return 'fees-commissions';
    }

    if (
      location.pathname.startsWith('/connections') ||
      location.pathname.startsWith('/connection/')
    ) {
      return 'connections';
    }
  }, [location.pathname]);
}

const Ledger = () => {
  const { isReadOnly } = useBilling();
  const { GL_WITH_LEGACY_AUTOMATIONS } = useEnabledFeatures();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const type = usePageType();

  const getCurrentStatementsLink = () => {
    const base = '/statements/owner-statements';

    if (location.pathname.startsWith('/statement')) {
      const date = searchParams.get('date');

      return `${base}${date ? `?date=${date}` : ''}`;
    }

    return base;
  };

  return (
    <>
      <Section>
        <NavbarRoute
          title="Dashboard"
          link="/"
          icon={<Icon icon="DashboardIcon" size={ICON_SIZE} />}
          notification={isReadOnly ? null : <LedgerIssueCountBadge />}
        />
      </Section>

      <Section>
        <NavbarRoute
          title={'Bank Reconciliation'}
          link={'/reconciliation'}
          icon={<Icon icon={'FileInvoiceIcon'} size={ICON_SIZE} />}
        />

        {GL_WITH_LEGACY_AUTOMATIONS && (
          <NavbarRoute
            title={'Payments'}
            link={'/payments'}
            icon={<Icon icon={'CircleDollarIcon'} size={ICON_SIZE} />}
          />
        )}

        <NavbarRoute
          title="Deposits"
          link="/deposits"
          active={type === 'deposits'}
          icon={<Icon icon="PlusCircleIcon" size={ICON_SIZE} />}
        />
        <NavbarRoute
          title="Expenses"
          link="/expenses"
          active={type === 'expenses'}
          icon={<Icon icon="MinusCircleIcon" size={ICON_SIZE} />}
        />
        <NavbarRoute
          title="Reservations"
          link="/reservations"
          icon={<Icon icon="CalendarEventIcon" size={ICON_SIZE} />}
        />
        <NavbarRoute
          title="Listings"
          link="/listings"
          active={type === 'listings'}
          icon={<Icon icon="HomeIcon" size={ICON_SIZE} />}
        />

        <NavbarRoute
          title={'Owners'}
          link={'/owners'}
          active={type === 'owners'}
          icon={<Icon icon="UserIcon" size={ICON_SIZE} />}
        />

        {GL_WITH_LEGACY_AUTOMATIONS && (
          <NavbarRoute
            title="Mappings"
            link="/mappings"
            icon={<Icon icon="RouteIcon" size={ICON_SIZE} />}
          />
        )}

        <NavbarRoute
          title="Fees and Commissions"
          link="/fees-commissions"
          icon={<Icon icon="PercentageIcon" size={ICON_SIZE} />}
          active={type === 'fees-commissions'}
        />

        {GL_WITH_LEGACY_AUTOMATIONS && (
          <NavbarRoute
            title="Automations"
            link="/automations"
            icon={<Icon icon="AutomationsIcon" size={ICON_SIZE} />}
          />
        )}

        <NavbarRoute
          title="Reports"
          link="/reports"
          icon={<Icon icon="ListUnorderedIcon" size={ICON_SIZE} />}
        />
        <NavbarRoute
          title="Statements"
          link={getCurrentStatementsLink()}
          icon={<Icon icon="ClipboardTextIcon" size={ICON_SIZE} />}
          active={type === 'statements'}
        />
      </Section>

      <Section>
        <NavbarRoute
          title="Connections"
          link="/connections"
          icon={<Icon icon="ShareIcon" size={ICON_SIZE} />}
          notification={<NavbarSyncsIconButton />}
          active={type === 'connections'}
        />

        <NavbarRoute
          title="Files"
          link="/files"
          icon={<Icon icon={'FolderOpenIcon'} size={ICON_SIZE} />}
        />
        <NavbarRoute
          title="Settings"
          link="/settings"
          icon={<Icon icon="GearIcon" size={ICON_SIZE} />}
        />
      </Section>

      <LegacyMappings />
    </>
  );
};

const LedgerIssueCountBadge = () => {
  const data = useTeamIssues();
  return <BaseIssueCountBadge data={data} loading={false} />;
};

const LegacyIssueCountBadge = () => {
  const { data, isInitialLoading } = useLegacyIssuesQuery();
  return <BaseIssueCountBadge data={data} loading={isInitialLoading} />;
};

function BaseIssueCountBadge({
  data,
  loading,
}: {
  data: ReturnType<typeof useLegacyIssuesQuery>['data'];
  loading: ReturnType<typeof useLegacyIssuesQuery>['isInitialLoading'];
}) {
  const [{ isGL }] = useTeam();
  const goto = useNavigate();

  const count = data?.aggregate || 0;

  if (loading)
    return <CountBadge count={count} loading={loading} loadingColor="white" />;

  if (!count) return null;

  return (
    <IconButton
      onClick={(event) => {
        if (isGL) return;

        event.preventDefault();
        event.stopPropagation();
        goto('/dashboard/issues');
      }}
      sx={{
        width: 'unset',
      }}
    >
      <CountBadge count={count} loading={loading} loadingColor="white" />
    </IconButton>
  );
}

function UserGuide() {
  const setGuide = useSetupGuideStore((state) => state.setSetupGuide);

  const [{ id: teamId }] = useTeam();
  const _setup = useSubscription(
    (q, args) => {
      if (!args.teamId)
        return {
          tenant: null,
        };

      const tenant = q
        .tenant({
          where: {
            id: { _eq: args.teamId },
          },
          limit: 1,
        })
        .map(
          (tenant) => (tenant.setup() || null) as TenantSetupGuide | null
        )?.[0];

      return {
        tenant: tenant || null,
      };
    },
    {
      teamId,
    },
    {
      skip: !teamId,
      queryKey: ['setupGuide'],
      onSubscriptionData: (data) => {
        if (data) setGuide(data.tenant);
      },
    }
  );

  return (
    <Section>
      <NavbarRoute
        title="Getting started"
        link="/getting-started"
        icon={<Icon icon="Loader2Icon" size={ICON_SIZE} />}
      />
    </Section>
  );
}

const NavigationSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const { isDesktopExpanded } = useNavbarExpanded();

  return (
    <Box>
      {title && isDesktopExpanded && (
        <Text
          component="span"
          mb="xs"
          c="white"
          pl={5}
          sx={{ opacity: 0.5, display: 'block' }}
        >
          {title}
        </Text>
      )}
      <Stack gap="xs">{children}</Stack>
    </Box>
  );
};

function LegacyMappings() {
  // LEGACY
  const [dashboard] = useDashboard();
  const [{ partnerId, id: teamId }] = useTeam();

  const { data } = useQuery(
    (q, args) => {
      const retoolViews = q
        .views({
          where: {
            tenantId: { _eq: args.teamId },
            group: { _eq: 'Reports' },
          },
          order_by: [{ sortOrder: 'asc_nulls_last' }],
        })
        .map((view) => ({
          id: view.id!,
          title: view.title || '',
          url: view.retoolViewId!,
          group: view.group?.toLowerCase() || '',
        }));

      const hasPmsConnection = !!getPropertyManagementConnections(
        q,
        { teamId: args.teamId },
        { limit: 1 }
      ).length;

      return {
        retoolViews,
        hasPmsConnection,
      };
    },
    {
      queryKey: 'views',
      variables: {
        teamId,
      },
    }
  );

  const retoolViews = data?.retoolViews || [];

  const showMappingsV2 =
    partnerId === XIMPLIFI_TENANT_ID &&
    data !== undefined &&
    !data.hasPmsConnection;

  const reViews = retoolViews.filter(
    (x) => !showMappingsV2 || x.url !== '11ef2837-7612-48db-a7c7-68c621d41efc'
  );

  if (!showMappingsV2 && !reViews?.length && dashboard !== 'partner') {
    return null;
  }

  return (
    <NavigationSection title={'Legacy'}>
      {/* LEGACY */}
      {showMappingsV2 && (
        <NavbarRoute
          title="Mappings"
          link="/reports/11ef2837-7612-48db-a7c7-68c621d41efc"
          icon={<Icon icon="FileTextIcon" size={ICON_SIZE} />}
        />
      )}
      {reViews?.length > 0 && (
        <NavbarRoute
          title="Reports"
          link={`/${retoolViews[0].group}/${retoolViews[0].url}`}
          icon={<Icon icon="FileTextIcon" size={ICON_SIZE} />}
          subRoutes={retoolViews}
        />
      )}
    </NavigationSection>
  );
}

// const SETUP_GUIDE_JSON: TenantSetupGuide = {
//   'e0de569c-7c39-48ed-bb2e-f654aa1c0e32': {
//     init: {
//       label: 'Init',
//       state: 'done',
//       action: {
//         key: 'init',
//         type: 'automation',
//         automationTemplate: 'quickbooksAccounting_initialData',
//       },
//       description: 'Creates items & accounts',
//     },
//     accountingConnection: {
//       appId: 'quickbooks',
//       connectionId: 'e0de569c-7c39-48ed-bb2e-f654aa1c0e32',
//     },
//     customFees: {
//       label: 'Management Commission',
//       state: 'notStarted',
//       action: {
//         key: 'managementCommission',
//         type: 'automation',
//         automationTemplate: 'quickbooksAccounting_commission',
//       },
//       description: 'Management Commission',
//     },
//     automations: {
//       invoice: {
//         label: 'Invoice',
//         state: 'notStarted',
//         action: {
//           key: 'invoice',
//           type: 'automation',
//           automationTemplate: 'quickbooksAccounting_invoice',
//         },
//         description: 'Invoice',
//       },
//       payment: {
//         label: 'Payment',
//         state: 'notStarted',
//         action: {
//           key: 'payment',
//           type: 'automation',
//           automationTemplate: 'quickbooksAccounting_payment',
//         },
//         description: 'Payment',
//       },
//       journalEntry: {
//         label: 'Journal Entry',
//         state: 'notStarted',
//         action: {
//           key: 'journalEntry',
//           type: 'automation',
//           automationTemplate: 'quickbooksAccounting_journalEntry',
//         },
//         description: 'Journal Entry',
//       },
//     },
//     pmsConnection: {
//       appId: 'hostfully',
//       connectionId: '9c33f72e-8a09-49f3-b4c0-8cfed7497fda',
//     },
//     prerequisites: [],
//     ownerStatements: {
//       label: 'Owner Statement',
//       state: 'notStarted',
//       action: {
//         key: 'ownerStatement',
//         type: 'automation',
//         automationTemplate: 'quickbooksAccounting_ownerStatements',
//       },
//       description: 'Owner Statement',
//     },
//     listingsAndOwners: {
//       importOwners: {
//         label: 'Import Owners',
//         state: 'notStarted',
//         action: {
//           key: 'owner',
//           type: 'extract',
//         },
//         description: 'Import Owners',
//       },
//       setupClasses: {
//         label: 'Setup Classes',
//         state: 'done',
//         action: {
//           key: 'class',
//           type: 'automation',
//           automationTemplate: 'quickbooksAccounting_class',
//         },
//         description: 'Setup Classes',
//       },
//       setupVendors: {
//         label: 'Setup Vendors',
//         state: 'notStarted',
//         action: {
//           key: 'vendor',
//           type: 'automation',
//           automationTemplate: 'quickbooksAccounting_owner',
//         },
//         description: 'Setup Vendors',
//       },
//       importListings: {
//         label: 'Import Listings',
//         state: 'notStarted',
//         action: {
//           key: 'listing',
//           type: 'extract',
//         },
//         description: 'Import Listings',
//       },
//     },
//   },
// };
